import { ExclamationCircleIcon } from "@heroicons/react/solid";
import { Cross1Icon } from "@radix-ui/react-icons";
import * as Sentry from "@sentry/nextjs";
import { differenceInDays, format } from "date-fns";
import React, { useRef, useState } from "react";
import { useIsomorphicLayoutEffect } from "react-use";
import { useChat } from "ui/components/chat/container";
import { ChevronLeft } from "ui/components/icons";
import { Spinner } from "ui/components/icons/Spinner";
import Star from "ui/components/icons/Star";
import StarFilled from "ui/components/icons/StarFilled";
import { getMemberHealth } from "ui/lib/getMemberHealth";
import { getValidDate } from "ui/lib/helpers";
import { useEditable } from "ui/lib/useEditable";
import { handleUpdateRequest } from "ui/lib/useRequest";
import { cn } from "ui/lib/utils";
import {
  STATUS_PILL,
  STATUS_TEXT,
  healthTone,
  membershipTone,
} from "apollo/lib/status-tone";

interface NotesComponentProps {
  selectedUserId: string;
  remoteNotes: string;
  /** Grows with its text and has no resize grip, for touch. */
  autoGrow?: boolean;
}
interface GeneralProps {
  value: string;
}

export const NotesComponent: React.FC<NotesComponentProps> = React.memo(
  ({ selectedUserId, remoteNotes, autoGrow }) => {
    const [notesSaving, setNotesSaving] = useState(false);
    const fieldRef = useRef<HTMLTextAreaElement>(null);
    const [error, setError] = useState(false);

    const updateNotes = async (value) => {
      try {
        setNotesSaving(true);
        await handleUpdateRequest(`users/${selectedUserId}`, {
          notes: value,
        });
        setNotesSaving(false);
        setError(false);
      } catch (error) {
        setNotesSaving(false);
        setError(true);
        Sentry.captureException(error);
      }
    };

    const { value: notes, handleChange } = useEditable(
      remoteNotes,
      updateNotes
    );

    useIsomorphicLayoutEffect(() => {
      const field = fieldRef.current;
      if (!autoGrow || !field) return;
      field.style.height = "auto";
      field.style.height = `${field.scrollHeight}px`;
    }, [autoGrow, notes]);

    return (
      <div className="relative mt-4 text-foreground">
        <div className="absolute bottom-0 right-0 mb-4 mr-4">
          {notesSaving ? (
            <Spinner className="w-[20px] h-[20px] text-white" />
          ) : null}
        </div>
        <div>
          {error ? (
            <div className="p-2 mb-2 border bg-error/25 border-error rounded-xl">
              <span className="block text-sm leading-snug text-foreground">
                We are having trouble saving your update.
                <button
                  onClick={() => updateNotes(notes)}
                  className="px-2 py-1 font-bold bg-white/25 rounded"
                >
                  Try Again
                </button>
              </span>
            </div>
          ) : null}
          <textarea
            ref={fieldRef}
            value={notes}
            style={{ minHeight: autoGrow ? 120 : 260 }}
            placeholder="Add Notes"
            aria-label="Notes"
            className={cn(
              "w-full p-3 rounded-xl shadow-input bg-card",
              autoGrow && "resize-none overflow-hidden text-base"
            )}
            onChange={handleChange}
          />
        </div>
      </div>
    );
  }
);

interface ErrorPillProps {
  value: string;
  condition: boolean;
}

export const ErrorPill: React.FC<ErrorPillProps> = React.memo(
  ({ value, condition }) => {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1",
          condition && ["font-medium", STATUS_TEXT.attention]
        )}
      >
        {condition ? (
          <ExclamationCircleIcon aria-hidden="true" className="size-4" />
        ) : null}
        {value}
        {condition ? <span className="sr-only">, overdue</span> : null}
      </span>
    );
  }
);

interface RoleProps {
  value: "member" | "admin" | "coach" | "friend" | "trialler" | "expired";
}

export const Role: React.FC<RoleProps> = React.memo(({ value }) => {
  return (
    <div
      className={cn("rounded px-3 font-bold", {
        "bg-grass text-foreground": value === "admin",
        "bg-royal text-foreground": value === "member",
        "bg-primary text-black": value === "trialler",
        "bg-background text-foreground": value === "expired",
        "bg-medium-grey text-foreground": ![
          "member",
          "trialler",
          "expired",
        ].includes(value),
      })}
    >
      <span
        style={{ textShadow: "0px 1px 2px #0000003b" }}
        className="text-sm uppercase"
      >
        {value}
      </span>
    </div>
  );
});

interface StatusProps {
  value: string;
  label?: string;
}

export const Status: React.FC<StatusProps> = React.memo(
  ({ value, label = "" }) => {
    if (!value) return null;
    return (
      <div className={cn("rounded-full", STATUS_PILL[membershipTone(value)])}>
        <span className="px-2 text-xs font-bold uppercase">{label}</span>
      </div>
    );
  }
);

export const LastActive: React.FC<GeneralProps> = React.memo(({ value }) => {
  const daysAgo = value ? differenceInDays(new Date(), getValidDate(value)) : 0;
  return (
    <div className="flex items-center justify-between py-2">
      <label className="text-[13px] text-muted-foreground">Last active</label>
      <ErrorPill
        value={value ? (daysAgo ? `${daysAgo} days ago` : "Today") : "-"}
        condition={daysAgo >= 10}
      />
    </div>
  );
});

export const LastWorkout: React.FC<GeneralProps> = React.memo(({ value }) => {
  const daysAgo = value ? differenceInDays(new Date(), getValidDate(value)) : 0;
  return (
    <div className={cn("flex justify-between items-center py-2 ", {})}>
      <label className="text-[13px] text-muted-foreground">Last workout</label>
      <ErrorPill
        value={value ? (daysAgo ? `${daysAgo} days ago` : "Today") : "-"}
        condition={daysAgo >= 7}
      />
    </div>
  );
});

export const UserCreated: React.FC<GeneralProps> = React.memo(({ value }) => {
  const date = value ? format(getValidDate(value), "MMM d, y") : null;
  return (
    <div className="flex items-center justify-between py-2">
      <label className="text-[13px] text-muted-foreground">User created</label>
      <span>{value ? date : "-"}</span>
    </div>
  );
});

export const TrialExpiration: React.FC<GeneralProps> = React.memo(
  ({ value }) => {
    const date = value ? format(getValidDate(value), "MMM d, y") : null;
    return (
      <div className="flex items-center justify-between py-2">
        <label className="text-[13px] text-muted-foreground">
          Trial expiration
        </label>
        <span>{value ? date : "-"}</span>
      </div>
    );
  }
);

export const RenewalDate: React.FC<GeneralProps> = React.memo(({ value }) => {
  const date = value ? format(getValidDate(value), "MMM d, y") : null;
  return (
    <div className="flex items-center justify-between py-2">
      <label className="text-[13px] text-muted-foreground">Renewal date</label>
      <span>{value ? date : "-"}</span>
    </div>
  );
});

export const SubscriptionType: React.FC<GeneralProps> = React.memo(
  ({ value = "-" }) => {
    return (
      <div className="flex items-center justify-between py-2">
        <label className="text-[13px] text-muted-foreground">
          Subscription type
        </label>
        <span>{value}</span>
      </div>
    );
  }
);

export const UserRole: React.FC<RoleProps> = React.memo(({ value }) => {
  return (
    <div className="flex items-center justify-between py-2">
      <label className="text-[13px] text-muted-foreground">Role</label>
      <div className="flex">
        <Role value={value} />
      </div>
    </div>
  );
});

interface MemberHealthProps {
  lastWOCompletedAt: string;
  lastActivityAt: string;
}

export const MemberHealth: React.FC<MemberHealthProps> = React.memo(
  ({ lastWOCompletedAt, lastActivityAt }) => {
    const value = getMemberHealth({ lastWOCompletedAt, lastActivityAt });

    return (
      <div className="flex items-center justify-between py-2">
        <label className="text-[13px] text-muted-foreground">Health</label>
        <span
          className={cn(
            "font-medium capitalize",
            STATUS_TEXT[healthTone(value)]
          )}
        >
          {value?.toLowerCase?.()}
        </span>
      </div>
    );
  }
);

interface UserRoleAndStatusProps {
  role: string;
  status: string;
}

export const UserRoleAndStatus: React.FC<UserRoleAndStatusProps> = React.memo(
  ({ role, status }) => {
    return (
      <div className="flex items-center justify-between py-2">
        <label className="text-[13px] text-muted-foreground">Status</label>
        <div className="flex">
          <Status value={status} label={`${status} ${role}`} />
        </div>
      </div>
    );
  }
);

const splitTags = (value?: string | null) =>
  value
    ?.split(";")
    .filter(Boolean)
    .filter((val) => val !== " ") ?? [];

// Two rows of 24px tags with the 6px gap between them.
const TWO_ROWS = "max-h-[3.375rem]";

/** Tags under their label, two rows at most until "+N more". */
const StackedTags = ({ tags }: { tags: string[] }) => {
  const listRef = useRef<HTMLUListElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [hidden, setHidden] = useState(0);
  useIsomorphicLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const measure = () => {
      const items = [...list.children] as HTMLElement[];
      const rows = [...new Set(items.map((item) => item.offsetTop))].sort(
        (a, b) => a - b
      );
      const lastShown = rows[1] ?? rows[0];
      setHidden(items.filter((item) => item.offsetTop > lastShown).length);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    return () => observer.disconnect();
  }, [tags]);

  return (
    <div className="py-2">
      <span className="text-[13px] text-muted-foreground">Tags</span>
      {tags.length ? (
        <>
          <ul
            ref={listRef}
            className={cn(
              "mt-1.5 flex flex-wrap gap-1.5 overflow-hidden text-xs",
              !expanded && TWO_ROWS
            )}
          >
            {tags.map((tag, n) => (
              <li
                key={n}
                className={cn("rounded-full px-2 py-1", STATUS_PILL.neutral)}
              >
                {tag}
              </li>
            ))}
          </ul>
          {hidden || expanded ? (
            <button
              type="button"
              aria-expanded={expanded}
              onClick={() => setExpanded(!expanded)}
              className="-ml-2 mt-1 min-h-11 rounded-lg px-2 text-[13px] font-medium text-foreground/80 active:bg-white/[0.06]"
            >
              {expanded ? "Show less" : `+${hidden} more`}
            </button>
          ) : null}
        </>
      ) : (
        <p className="mt-1">-</p>
      )}
    </div>
  );
};

export const Tags: React.FC<GeneralProps & { stacked?: boolean }> = React.memo(
  ({ value, stacked }) => {
    const tags = splitTags(value);
    if (stacked) return <StackedTags tags={tags} />;
    return (
      <div className="flex items-center justify-between py-2 space-x-4">
        <label className="text-[13px] text-muted-foreground">Tags</label>
        <span className="flex flex-wrap justify-end gap-x-2 text-xs">
          {value != null
            ? tags.map((val, n) => (
                <span
                  className={cn(
                    "block px-2 py-1 mb-1 rounded-full flex-center",
                    STATUS_PILL.neutral
                  )}
                  key={n}
                >
                  {val}
                </span>
              ))
            : "-"}
        </span>
      </div>
    );
  }
);

interface RatingProps {
  value: number;
  label: string;
}

export const Rating: React.FC<RatingProps> = React.memo(({ value, label }) => {
  const rating =
    typeof value === "number" && Number.isFinite(value)
      ? Math.min(5, Math.max(0, value))
      : null;
  const stars = rating === null ? null : Math.round(rating);
  return (
    <div className="flex items-center justify-between py-2">
      <label className="text-[13px] text-muted-foreground">{label}</label>
      {stars === null ? (
        <span>-</span>
      ) : (
        // One image for the row: the stars alone read as five unnamed graphics.
        <div
          role="img"
          aria-label={`${Number(rating.toFixed(1))} out of 5`}
          className="flex"
        >
          {[...Array(stars)].map((_, ndx) => (
            <StarFilled
              key={ndx}
              aria-hidden="true"
              className={cn(
                "w-5 h-5",
                stars > 3 ? STATUS_TEXT.primary : STATUS_TEXT.attention
              )}
            />
          ))}
          {[...Array(5 - stars)].map((_, ndx) => (
            <Star
              key={ndx}
              aria-hidden="true"
              className="w-5 h-5 text-foreground"
            />
          ))}
        </div>
      )}
    </div>
  );
});

interface NumberProps {
  value: number;
}

export const LastWorkoutRating: React.FC<NumberProps> = React.memo(
  ({ value }) => {
    return <Rating label="Last workout rating" value={value} />;
  }
);

export const AverageWorkoutRating: React.FC<NumberProps> = React.memo(
  ({ value }) => {
    return <Rating label="Average workout rating" value={value} />;
  }
);

export const Header = React.memo(() => {
  const { setInfoPanelActive } = useChat();

  return (
    <div className="absolute z-50 top-6 left-4 md:left-auto md:right-4 flex">
      <button
        aria-label="Close member info"
        className="p-2 w-auto center text-foreground bg-secondary shadow-button rounded-full"
        onClick={() => setInfoPanelActive(false)}
      >
        <Cross1Icon className="hidden w-4 h-4 md:block" />
        <ChevronLeft className="block w-5 md:hidden" />
      </button>
    </div>
  );
});
